import { useCallback, useMemo } from 'react'
import _ from 'lodash'
import { useDomainMessages } from 'domains/role-management/utils/domain/intl'
import {
  type AppFeature,
  type AppPermissionsKeys,
  PermissionsDictionary,
  numberOfDaysAPermissionIsNew,
  type AppPermission,
} from 'domains/role-management/components/role-assigned-permissions/PermissionsDictionary'
import { numberOfDaysSince } from 'domains/role-management/utils/hooks/useGetDaysSinceLastUpdated.hooks'
import { useGetPermissionsQuery } from 'domains/role-management/api'

export type TranslatedPermissionsDictionary = TranslatedFeatureGroup[]

// Constants representing the hierarchy permission groups
export const group1st: AppPermissionsKeys = 'group'
export const group2nd: AppPermissionsKeys = 'featureName'

// The prefix for ALL permission translation string keys
// EG: 'roleManagementDomain.permissions.administration'
// EG: 'roleManagementDomain.permissions.administration.userManagement'
// EG: 'roleManagementDomain.permissions.administration.userManagement.manage_users.legacy_permission'
const I18N_PREFIX = 'roleManagementDomain.permissions'

export interface TranslatedFeatureGroup {
  groupName: string
  features: TranslatedAppFeature[]
}

export interface TranslatedAppFeature {
  featureName: string
  description: string
  isNewPermission: boolean
  permissions: LabeledPermission[]
}

export interface LabeledPermission {
  slug: string
  label: string
}

/**
 * The following block of types are used to ensure that all external permissions have all required translation keys.
 * Each resulting type represents all required translation keys (i.e. groupKey, featureKey, featureKey.description and slugKeys).
 * These are used to ensure that all external permissions have all required translation keys else a type error will occur.
 */
type Prefix = typeof I18N_PREFIX
type PermissionsDictionaryType = typeof PermissionsDictionary

/**
 * Get all permission group translation keys.
 */
type PermissionGroupKeys = PermissionsDictionaryType[number]['group']

/**
 * Get all permission feature translation keys.
 * Feature translation keys include the group name (i.e. roleManagementDomain.permissions.{groupKey}.{featureKey}).
 * This means we need to create a dynamic type that pairs the group and feature keys.
 */
type PermissionGroupFeatures<T extends AppPermission = PermissionsDictionaryType[number]> = {
  [PermObj in T as `${PermObj['group']}.${PermObj['featureName']}`]: string
}
type PermissionGroupFeatureKeys = keyof PermissionGroupFeatures

/**
 * Get all permission slug translation keys.
 * Slug translation keys include the group and feature key (i.e. roleManagementDomain.permissions.{groupKey}.{featureKey}.{slugKey}).
 * This means we need to create dynamic a type that pairs the group, feature and slug keys.
 */
type PermissionGroupFeatureSlugs<T extends AppPermission = PermissionsDictionaryType[number]> = {
  [PermObj in T as `${PermObj['group']}.${PermObj['featureName']}.${PermObj['slug']}`]: string
}
type PermissionGroupFeatureSlugKeys = keyof PermissionGroupFeatureSlugs

/**
 * Combine all group, feature and slug translation keys into a single type for all expected translation keys.
 * This is used to ensure that all external permission have all required translation keys else a type error will occur.
 */
type TranslationRecordKeys =
  | PermissionGroupKeys
  | PermissionGroupFeatureKeys
  | `${PermissionGroupFeatureKeys}.description`
  | PermissionGroupFeatureSlugKeys
type allExternalPermissionTranslationKeys =
  | `${Prefix}.${PermissionGroupKeys}`
  | `${Prefix}.${PermissionGroupFeatureKeys}`
  | `${Prefix}.${PermissionGroupFeatureKeys}.description`
  | `${Prefix}.${PermissionGroupFeatureSlugKeys}`

const usePermissionDictionary = (roleId?: string, permissionSlugs?: string[]) => {
  // first fetch the official list of permissions that this use has access to according the backend system
  const { data, loading, error } = useGetPermissionsQuery({ variables: { roleId: roleId } })
  // next we need to use the 'data' and intersect it with `PermissionsDictionary`

  const applicablePermissions = useMemo(() => {
    if (loading || error || !data?.permissions) {
      return []
    }

    const backendSlugs = data.permissions.map(permission => permission.slug)

    const allowedPermissions = permissionSlugs
      ? PermissionsDictionary.filter(dictionaryEntry =>
          permissionSlugs.includes(dictionaryEntry.slug)
        )
      : PermissionsDictionary

    // Intersects the backend permissions with the permissions in our PermissionsDictionary
    const intersectedPermissions = allowedPermissions.filter(dictionaryEntry =>
      backendSlugs.includes(dictionaryEntry.slug)
    )

    return intersectedPermissions
  }, [data?.permissions, error, loading, permissionSlugs])

  return { permissions: applicablePermissions, loading, error }
}

/**
 * Resolves permissions in a grouped collection based on function.
 *
 * The permissions will be scoped either based on the provided role ID or the account
 * that is requesting the permissions. Only permissions assignable to the provided role (id) will be returned,
 * if provided. Otherwise, only permissions visible to the current user will be returned.
 *
 * The permissions will be filtered based on the provided permission slugs, if provided.
 *
 * The returned permissions will be internationalized based on the current locale. They will be also structured into
 * FeatureGroups and Features. See `TranslatedPermissionsDictionary` for the structure.
 *
 * @param roleId optionally, the role ID that the permissions should be scoped for
 * @param permissionSlugs optionally, the permission slugs to be filtered for
 * @returns a internationalized version of the PermissionsDictionary
 */
export const useGroupedPermissionsDictionary = (roleId?: string, permissionSlugs?: string[]) => {
  // Builds the translation string key for feature groups
  const i18nPathForFeatureGroup = useCallback((groupName: string) => `${groupName}`, [])

  // Builds the translation string key for features
  const i18nPathForFeature = useCallback(
    (groupName: string, featureName: string) =>
      `${i18nPathForFeatureGroup(groupName)}.${featureName}`,
    [i18nPathForFeatureGroup]
  )

  // Builds the translation string key for permissions
  const i18nPathForPermissionLabel = useCallback(
    (groupName: string, featureName: string, perm: string) =>
      `${i18nPathForFeature(groupName, featureName)}.${perm}`,
    [i18nPathForFeature]
  )

  const {
    permissions: applicablePermissions,
    loading,
    error,
  } = usePermissionDictionary(roleId, permissionSlugs)

  const groupedPermissions = useMemo(() => {
    // Takes the flattened permissions and groups them by permission group and feature name
    // The result is a hierarchical object like this:
    // {groupName: 'groupValue', features: [{featureName: 'featureNameValue', permissionSlugs: ['slug1','slug2']}, madeExternalDate: '' ...], ...}
    const hierarchicalPermissions = Object.entries(_.groupBy(applicablePermissions, group1st)).map(
      ([groupName, groupValues]) => {
        const features = Object.entries(_.groupBy(groupValues, group2nd)).map(
          ([featureName, featureValues]) => {
            const permissionSlugs = featureValues.map(value => value.slug)

            const maxMadeExternalDate = featureValues
              .map(value => value.madeExternalDate)
              .reduce((a, b) => (a < b ? a : b))

            return {
              featureName,
              permissionSlugs,
              isNewPermission:
                numberOfDaysSince(maxMadeExternalDate) <= numberOfDaysAPermissionIsNew,
            }
          }
        )

        return {
          groupName,
          features,
        }
      }
    )

    if (!permissionSlugs) {
      // if permissionSlugs array was not provided to scope on, just return all permissions
      return hierarchicalPermissions
    }

    // function for checking the feature has a non-empty intersection with the permissionSlugs prop
    const featureHasMatchingPerms = (feature: AppFeature) =>
      _.intersection(feature.permissionSlugs, permissionSlugs).length > 0

    // filter out the permissions in this dictionary that are not in the scoped permissionSlugs,
    // then filter out the empty feature groups
    return hierarchicalPermissions
      .map(group => ({
        ...group,
        features: group.features.filter(featureHasMatchingPerms),
      }))
      .filter(group => group.features.length > 0)
  }, [applicablePermissions, permissionSlugs])

  // Builds a record object of all the translation keys needed
  const i18nPaths = useMemo(
    () =>
      PermissionsDictionary.reduce((acc, { slug, group, featureName }) => {
        const groupPath = i18nPathForFeatureGroup(group)
        acc[groupPath] = `${I18N_PREFIX}.${groupPath}`

        const featurePath = i18nPathForFeature(group, featureName)
        acc[featurePath] = `${I18N_PREFIX}.${featurePath}`

        const featureDescriptionPath = featurePath.concat('.description')
        acc[featureDescriptionPath] = `${I18N_PREFIX}.${featureDescriptionPath}`

        const permissionPath = i18nPathForPermissionLabel(group, featureName, slug)
        acc[permissionPath] = `${I18N_PREFIX}.${permissionPath}`

        return acc
      }, {} as Record<TranslationRecordKeys, allExternalPermissionTranslationKeys>),
    [i18nPathForFeatureGroup, i18nPathForFeature, i18nPathForPermissionLabel]
  )

  // Fetch those translations
  // If a type error occurs here look at the end of the error message for missing i18n keys
  const i18n = useDomainMessages(i18nPaths)

  // Transform the 'PermissionsDictionary' into a 'TranslatedPermissionsDictionary'
  const translatedPermissionDictionary = useMemo(
    () =>
      groupedPermissions.map(group => ({
        groupName: i18n[i18nPathForFeatureGroup(group.groupName)],
        features: group.features.map(feature => ({
          featureName: i18n[i18nPathForFeature(group.groupName, feature.featureName)],
          description:
            i18n[i18nPathForFeature(group.groupName, feature.featureName).concat('.description')],
          permissions: feature.permissionSlugs.map(permission => ({
            slug: permission,
            label:
              i18n[i18nPathForPermissionLabel(group.groupName, feature.featureName, permission)],
          })),
          isNewPermission: feature.isNewPermission,
        })),
      })) as TranslatedPermissionsDictionary,
    [
      i18n,
      groupedPermissions,
      i18nPathForFeatureGroup,
      i18nPathForFeature,
      i18nPathForPermissionLabel,
    ]
  )

  return { dictionary: translatedPermissionDictionary, loading, error }
}
